import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck, Lock, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { EDGE, EDGE_NAVY, EDGE_APP_URL } from "../../lib/edge";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import Seo from "../../components/Seo";

const LOAN_TYPES = ["Purchase", "Refinance", "Cash-Out Refi", "HELOC", "Other"];
const PROGRAMS = ["Conventional", "FHA", "VA", "Jumbo", "USDA", "First-Time Buyer", "FHA 203(k) Rehab", "DSCR", "Investor Loans", "Reverse Mortgage", "Refinance", "HELOC / Home Equity", "Construction-to-Permanent", "Physician Loan", "ITIN Loan", "Not sure"];
const CREDIT = ["740+", "700–739", "660–699", "620–659", "Below 620", "Not sure"];
const TIMELINE = ["0–3 months", "3–6 months", "6–12 months", "Just exploring"];

export default function PreApproval() {
  const location = useLocation();
  const initialProgram = location.state?.program || "";

  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    loan_type: "Purchase",
    program: initialProgram,
    purchase_price: "",
    down_payment: "",
    annual_income: "",
    credit_range: "",
    timeline: "",
    message: "",
    consent_tcpa: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone || !f.loan_type) {
      toast.error("Please fill in your name, email, phone, and loan type.");
      return;
    }
    if (!f.consent_tcpa) {
      toast.error("Please confirm consent to continue.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        purchase_price: f.purchase_price ? Number(f.purchase_price) : null,
        down_payment: f.down_payment ? Number(f.down_payment) : null,
        annual_income: f.annual_income ? Number(f.annual_income) : null,
      };
      await api.post("/loan-application", payload);
      setDone(true);
      toast.success("Application sent! Pierre will reach out within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-stone-50 min-h-[80vh] flex items-center justify-center px-6 py-16">
        <Seo title="Pre-Approval Submitted" path="/pre-approval" />
        <div className="max-w-lg bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
          >
            <Check className="w-7 h-7" strokeWidth={2} />
          </div>
          <h1 className="font-display text-3xl font-semibold">You're in good hands.</h1>
          <p className="text-[#555] mt-4 leading-relaxed">
            Pierre will reach out within <strong>1 business day</strong> at{" "}
            <span className="font-medium">{f.email}</span> or {f.phone} to walk through next steps.
          </p>
          <p className="text-xs text-gray-400 mt-6">
            Need to reach us sooner? Call {EDGE.officer.name} directly at{" "}
            <a href={`tel:${EDGE.officer.phone.replace(/\D/g, "")}`} className="text-[#E63946] underline">
              {EDGE.officer.phone}
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="pre-approval-page">
      <Seo
        title="Get Pre-Approved"
        description="Apply for mortgage pre-approval with Edge Home Finance. Pierre Joseph, NMLS #1978260. Most decisions in 24 hours."
        path="/pre-approval"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Pre-Approval · 24-hour decision
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          Let's see what you can <em className="not-italic" style={{ color: EDGE_NAVY }}>afford.</em>
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          Choose how to get started — launch Edge Home Finance's official 1003 application, or leave a quick inquiry and Pierre will reach out within 1 business day.
        </p>
      </section>

      {/* PATHS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* PRIMARY: Official 1003 */}
        <div
          className="rounded-2xl p-8 lg:p-10 text-white flex flex-col"
          style={{ background: `linear-gradient(135deg, ${EDGE_NAVY} 0%, #172A4F 100%)` }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-4">
            Recommended · Official 1003
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter">
            Launch the Edge Home Finance application
          </h2>
          <p className="text-white/85 mt-4 leading-relaxed">
            Apply online through Edge Home Finance's secure 1003 portal — the standard mortgage application.
            Creates your file, captures required documentation, and routes directly to Pierre Joseph (NMLS #{EDGE.officer.nmls}).
          </p>
          <ul className="mt-5 space-y-2 text-sm text-white/90">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Encrypted bank-grade portal</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Save &amp; resume later</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Upload W-2s, paystubs, bank statements securely</li>
          </ul>
          <a
            href={EDGE_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto pt-8 inline-flex items-center justify-center gap-2 bg-white text-[#1A1A1A] hover:bg-stone-100 px-7 py-3.5 rounded-md font-semibold text-sm transition-colors"
            data-testid="pa-official-cta"
          >
            Start Official Application
            <ExternalLink className="w-4 h-4" />
          </a>
          <div className="text-[10px] text-white/60 mt-3 text-center">
            Opens {new URL(EDGE_APP_URL).hostname} in a new tab
          </div>
        </div>

        {/* SECONDARY: Quick inquiry */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 lg:p-10 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-4">
            Not ready to apply? · Quick Inquiry
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter text-[#1A1A1A]">
            Have Pierre reach out first
          </h2>
          <p className="text-[#555] mt-4 leading-relaxed">
            Just exploring? Share a few details and Pierre will call or email within 1 business day — no SSN required,
            no credit pull.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-[#333]">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#E63946]" /> No credit check</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#E63946]" /> Personal follow-up from Pierre</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#E63946]" /> Good for first-time or exploratory questions</li>
          </ul>
          <a
            href="#quick-inquiry"
            className="mt-auto pt-8 inline-flex items-center justify-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-7 py-3.5 rounded-md font-semibold text-sm transition-colors"
            data-testid="pa-inquiry-cta"
          >
            Leave a Quick Inquiry
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <section id="quick-inquiry" className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 scroll-mt-28">
        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-8 bg-white border border-gray-200 rounded-2xl p-7 lg:p-10 space-y-5"
          data-testid="pre-approval-form"
        >
          <Section title="Your details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="pa-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="pa-email" />
              </Field>
              <Field label="Phone" required>
                <input className={inp} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required data-testid="pa-phone" />
              </Field>
              <Field label="Annual income (gross)">
                <input type="number" className={inp} value={f.annual_income} onChange={(e) => setF({ ...f, annual_income: e.target.value })} placeholder="$" data-testid="pa-income" />
              </Field>
            </div>
          </Section>

          <Section title="The loan">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Loan type" required>
                <select className={inp} value={f.loan_type} onChange={(e) => setF({ ...f, loan_type: e.target.value })} data-testid="pa-loan-type">
                  {LOAN_TYPES.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Program (if known)">
                <select className={inp} value={f.program} onChange={(e) => setF({ ...f, program: e.target.value })} data-testid="pa-program">
                  <option value="">Select…</option>
                  {PROGRAMS.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Estimated purchase price">
                <input type="number" className={inp} value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} placeholder="$" data-testid="pa-price" />
              </Field>
              <Field label="Down payment available">
                <input type="number" className={inp} value={f.down_payment} onChange={(e) => setF({ ...f, down_payment: e.target.value })} placeholder="$" data-testid="pa-down" />
              </Field>
              <Field label="Credit range (estimate)">
                <select className={inp} value={f.credit_range} onChange={(e) => setF({ ...f, credit_range: e.target.value })} data-testid="pa-credit">
                  <option value="">Select…</option>
                  {CREDIT.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Timeline to close">
                <select className={inp} value={f.timeline} onChange={(e) => setF({ ...f, timeline: e.target.value })} data-testid="pa-timeline">
                  <option value="">Select…</option>
                  {TIMELINE.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          <Section title="Anything else?">
            <textarea
              rows={4}
              className={inp + " resize-none"}
              value={f.message}
              onChange={(e) => setF({ ...f, message: e.target.value })}
              placeholder="Goals, questions, or context Pierre should know…"
              data-testid="pa-message"
            />
          </Section>

          <label className="flex items-start gap-3 text-xs text-gray-600 leading-relaxed pt-2">
            <input
              type="checkbox"
              checked={f.consent_tcpa}
              onChange={(e) => setF({ ...f, consent_tcpa: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-[#E63946]"
              data-testid="pa-consent"
            />
            <span>
              I consent to be contacted by Edge Home Finance and Pierre Joseph (NMLS #{EDGE.officer.nmls}) by phone,
              SMS, or email regarding my mortgage inquiry. I understand consent is not a condition of any loan
              and that I can opt out at any time. I have read the Affiliated Business Arrangement Disclosure below.
            </span>
          </label>

          <button
            type="submit"
            disabled={busy}
            className="w-full sm:w-auto bg-[#E63946] hover:bg-[#c9313d] text-white px-8 py-3.5 rounded-md font-semibold text-sm disabled:opacity-50 transition-colors"
            data-testid="pa-submit"
          >
            {busy ? "Sending…" : "Submit Pre-Approval Request"}
          </button>
        </form>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-4">
          <SideCard icon={<ShieldCheck className="w-5 h-5" />} title="Why pre-approve first?">
            Sellers in Houston favor offers backed by a pre-approval letter — it shows you're serious and financed.
          </SideCard>
          <SideCard icon={<Lock className="w-5 h-5" />} title="Your data is secure">
            We don't ask for your SSN here. A soft inquiry can be initiated later only with your permission.
          </SideCard>
          <SideCard icon={<Check className="w-5 h-5" />} title="What we review">
            Loan type, target price, down payment, income, and credit range — enough for an initial review and
            program recommendation.
          </SideCard>
          <div className="bg-[#1A1A1A] text-white rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-2">
              Prefer to talk first?
            </div>
            <a
              href={`tel:${EDGE.officer.phone.replace(/\D/g, "")}`}
              className="font-display text-2xl font-semibold hover:underline"
            >
              {EDGE.officer.phone}
            </a>
            <div className="text-sm text-white/70 mt-1">{EDGE.officer.name} · NMLS #{EDGE.officer.nmls}</div>
          </div>
        </aside>
      </section>

      <MortgageDisclosure />
    </div>
  );
}

const inp =
  "w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E63946]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1.5">
        {label} {required && <span className="text-[#E63946]">*</span>}
      </div>
      {children}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">{title}</div>
      {children}
    </div>
  );
}

function SideCard({ icon, title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
      >
        {icon}
      </div>
      <div className="font-display font-semibold text-[#1A1A1A]">{title}</div>
      <p className="text-sm text-[#555] mt-1.5 leading-relaxed">{children}</p>
    </div>
  );
}
